import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoogleDriveSaver } from '../../../hooks/useGoogleDriveSaver';
import GoogleDriveIcon from '../../../components/icons/GoogleDriveIcon';
import {
  setSavedFolderTarget,
  type DriveTargetFolder,
} from '../../../utils/driveTargetFolder';
import { APP_FOLDER_NAME, type DriveOmfzFile } from '../../../utils/driveApi';
import { driveHistoryTitle } from '../../../utils/driveHistoryTitle';
import type { FingeringNavigateState } from '../../../types/fingering';
import { ArchiveError } from '../../../types/driveArchive';
// issue #110: storage 直の dismiss は snapshot を更新せず UI に残るため、
// broadcast まで行う jobTracker.dismissFailedJob を使う。
import { dismissFailedJob } from '../../../services/jobTracker';
import { useJobTracker } from '../../../contexts/JobTrackerContext';
import DriveHistoryModal from './DriveHistoryModal';
import OverwriteConfirmModal from './OverwriteConfirmModal';
import DeleteHistoryConfirmModal from './DeleteHistoryConfirmModal';
import RestoreLoadingOverlay from './RestoreLoadingOverlay';

// issue #24 UX Group B: Drive 復元の例外を日本語文言に変換。
// ArchiveError は code (missing_manifest / invalid_manifest / oversize / unsupported_schema /
// checksum_mismatch / not_authorized / network / quota_exceeded / unknown) で分岐。
// それ以外の Error は message 内容で 401 / quota を分類、 fallback は復元失敗 message を生で返す。
function mapRestoreError(err: unknown): string {
  if (err instanceof ArchiveError) {
    switch (err.code) {
      case 'missing_manifest':
      case 'checksum_mismatch':
        return 'ファイルが壊れているか、 おまかせ運指くんの形式ではありません。';
      case 'invalid_manifest':
      case 'unsupported_schema':
        return 'ファイル形式が古いか、 対応していません。';
      case 'oversize':
        return 'ファイルサイズが大きすぎて読み込めません。';
      case 'not_authorized':
        return 'Google Drive の認証が切れました。 再ログインしてください。';
      case 'network':
        return 'ネットワークエラーで復元できませんでした。 通信状態を確認して再試行してください。';
      case 'quota_exceeded':
        return '保存領域が不足しています。 ブラウザのストレージを整理してください。';
      default:
        return `ファイル展開に失敗しました: ${err.message}`;
    }
  }
  if (err instanceof Error) {
    const lower = err.message.toLowerCase();
    if (lower.includes('quota')) {
      return '保存領域が不足しています。 ブラウザのストレージを整理してください。';
    }
    if (lower.includes('401') || lower.includes('unauthorized')) {
      return 'Google Drive の認証が切れました。 再ログインしてください。';
    }
    return err.message;
  }
  return '予期しないエラーが発生しました。 再度お試しください。';
}

// issue #138: Drive 履歴削除の例外を日本語文言に変換。 削除は元 PDF を残す操作なので
// 復元系とは別文言にする。 ArchiveError の code (not_authorized / network / quota_exceeded) で分岐。
function mapDeleteError(err: unknown): string {
  if (err instanceof ArchiveError) {
    switch (err.code) {
      case 'not_authorized':
        return '削除に失敗しました: Google Drive の認証が切れました。 再ログインしてください。';
      case 'network':
        return '削除に失敗しました: 通信状態を確認して再試行してください。';
      case 'quota_exceeded':
        return '削除に失敗しました: Drive 側で拒否されました。 時間をおいて再試行してください。';
      default:
        return `削除に失敗しました: ${err.message}`;
    }
  }
  if (err instanceof Error) return `削除に失敗しました: ${err.message}`;
  return '削除に失敗しました。 再度お試しください。';
}

interface DriveFooterBarProps {
  /** 保存先フォルダ。SSOT は親 (FingeringPage) の driveTarget state。null は既定 APP_FOLDER。 */
  target: DriveTargetFolder | null;
  onChangeFolder: (folder: DriveTargetFolder) => void;
  // issue #86: open 状態を親 (FingeringPage) にリフト。fetch/restore は本コンポーネントに温存。
  isHistoryOpen: boolean;
  onRequestOpenHistory: () => void;
  onCloseHistory: () => void;
  // issue #116 FE-2: 失敗ジョブの「再投入」。 即投入せず親 (FingeringPage) のフォーム復元へ委譲する
  // (親が履歴を閉じてトップのフォームへ抽出済み PDF + 元設定を復元 + スクロールする)。
  onRestoreJob: (jobId: string) => void;
}

const DriveFooterBar: React.FC<DriveFooterBarProps> = ({
  target,
  onChangeFolder,
  isHistoryOpen,
  onRequestOpenHistory,
  onCloseHistory,
  onRestoreJob,
}) => {
  const {
    isConfigured,
    isLoggedIn,
    hasFullAccess,
    needsReauth,
    pickFolder,
    listHistory,
    restoreFromHistory,
    deleteFromHistory,
    logout,
    reauthenticate,
  } = useGoogleDriveSaver();
  const navigate = useNavigate();
  // issue #76 Phase 3 sub-H: JobTracker を結線 (DriveHistoryModal 3 セクション化の駆動源)。
  const { activeJobs, recentlyFailedJobs, needsDriveCheckJobs } = useJobTracker();
  const [isPicking, setIsPicking] = useState(false);

  const [historyItems, setHistoryItems] = useState<DriveOmfzFile[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [overwriteOpen, setOverwriteOpen] = useState(false);
  // issue #146: 復元対象の id と表示名 (.omfz 除去済み) は 1 セットで扱うため単一 state にまとめる。
  // issue #177: pdfFileId (omfz appProperties 由来) も併せて持ち、 復元時にリンク取得を堅牢化する。
  const [pendingFile, setPendingFile] = useState<{ id: string; name: string; pdfFileId?: string } | null>(null);
  // issue #24 UX Group B: 復元中 overlay の state。 復元成功で即 false / 失敗で error 表示 (overlay は閉じない)。
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  // issue #138: 履歴削除の state。 pendingDeleteId=確認待ち / deletingId=削除実行中 / deleteError=失敗文言。
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleChangeFolder = useCallback(async () => {
    setIsPicking(true);
    try {
      const picked = await pickFolder();
      if (picked) {
        setSavedFolderTarget(picked);
        onChangeFolder(picked);
      }
    } finally {
      setIsPicking(false);
    }
  }, [pickFolder, onChangeFolder]);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const items = await listHistory();
      setHistoryItems(items);
    } catch (err) {
      setHistoryError(err instanceof Error ? err.message : '履歴取得に失敗しました');
    } finally {
      setHistoryLoading(false);
    }
  }, [listHistory]);

  // issue #86: isHistoryOpen の false→true 遷移で履歴を fetch (旧 handleOpenHistory の open+fetch を分離)。
  const prevHistoryOpenRef = useRef(false);
  useEffect(() => {
    if (isHistoryOpen && !prevHistoryOpenRef.current) {
      void fetchHistory();
    }
    prevHistoryOpenRef.current = isHistoryOpen;
  }, [isHistoryOpen, fetchHistory]);

  const handleSelectHistory = useCallback((file: DriveOmfzFile) => {
    // issue #177: 復元後のリンク取得を堅牢化するため完成PDFの fileId を引き回す (欠落する旧 omfz は undefined)。
    setPendingFile({ id: file.id, name: driveHistoryTitle(file), pdfFileId: file.appProperties?.pdfFileId });
    setOverwriteOpen(true);
  }, []);

  const handleConfirmRestore = useCallback(async () => {
    if (!pendingFile) return;
    // issue #24 UX Group B: OverwriteConfirmModal + DriveHistoryModal を即 close し、
    // 上位 RestoreLoadingOverlay (z-[60]) が背景操作を遮断する。
    setOverwriteOpen(false);
    onCloseHistory();
    setRestoreLoading(true);
    setRestoreError(null);
    try {
      // issue #177: pdfFileId があれば復元後のリンク取得堅牢化のため引き回す。 欠落する旧 omfz は
      // 従来どおり単一引数で呼ぶ (呼出契約の後方互換を維持)。
      if (pendingFile.pdfFileId) {
        await restoreFromHistory(pendingFile.id, pendingFile.pdfFileId);
      } else {
        await restoreFromHistory(pendingFile.id);
      }
      // 成功: overlay 即 close (loadAndShowResult は FingeringPage の restored useEffect 経由で発火)。
      const navState: FingeringNavigateState = { restored: true, restoredAt: Date.now() };
      navigate('/fingering', { replace: true, state: navState });
      setRestoreLoading(false);
    } catch (err) {
      // 失敗: overlay は閉じず error 状態に切替、 ユーザーが「閉じる」 で dismiss。
      setRestoreError(mapRestoreError(err));
    } finally {
      setPendingFile(null);
    }
  }, [pendingFile, restoreFromHistory, navigate, onCloseHistory]);

  const handleCancelRestore = useCallback(() => {
    setOverwriteOpen(false);
    setPendingFile(null);
  }, []);

  const handleDismissRestoreError = useCallback(() => {
    setRestoreLoading(false);
    setRestoreError(null);
  }, []);

  // issue #138: 履歴 item の削除ボタン → 確認ダイアログ open。
  const handleRequestDelete = useCallback((file: DriveOmfzFile) => {
    setDeleteError(null);
    setPendingDeleteId(file.id);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setPendingDeleteId(null);
  }, []);

  // issue #138: 確認後の削除実行。 当該行を deletingId で disable → deleteFromHistory →
  // 成功で historyItems から除去、 失敗は一覧を維持したまま deleteError を表示する (一覧非破壊)。
  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDeleteId) return;
    const targetId = pendingDeleteId;
    setPendingDeleteId(null);
    setDeletingId(targetId);
    setDeleteError(null);
    try {
      await deleteFromHistory(targetId);
      setHistoryItems((items) => items.filter((f) => f.id !== targetId));
    } catch (err) {
      setDeleteError(mapDeleteError(err));
    } finally {
      setDeletingId(null);
    }
  }, [pendingDeleteId, deleteFromHistory]);

  if (!isConfigured) return null;

  const folderLabel = target?.name ?? APP_FOLDER_NAME;

  return (
    <>
      <div className="sm:max-w-md mx-auto mb-3 px-1">
        <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-sky-50 to-blue-50 border border-blue-100 rounded-xl shadow-sm">
          {/* issue #47: 保存先カードを左 (flex-1)、 履歴ボタンを右 (flex-shrink-0) に配置 */}
          <button
            type="button"
            onClick={handleChangeFolder}
            disabled={isPicking}
            aria-label="保存先を変更"
            className="flex items-center gap-2 min-w-0 flex-1 rounded-lg px-1 py-0.5 -mx-1 text-left hover:bg-white/60 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <GoogleDriveIcon className="w-4 h-4 flex-shrink-0" />
            <span className="text-xs text-gray-500 flex-shrink-0">保存先:</span>
            <span className="text-sm font-bold text-gray-700 truncate">
              {folderLabel}
            </span>
            <span aria-hidden="true" className="text-xs text-blue-400 flex-shrink-0">📁</span>
          </button>
          {/* issue #47: 識別背景色 (bg-primary-500=#3b9aee) で履歴導線を目立たせる */}
          <button
            type="button"
            onClick={onRequestOpenHistory}
            aria-label="履歴を見る"
            className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold bg-primary-500 text-white hover:bg-primary-600 transition-colors flex-shrink-0"
          >
            <span aria-hidden="true" className="text-base">📚</span>
            <span>履歴</span>
          </button>
        </div>
        {/* issue #35 M-2 + M-3a + PR3 / #123: ログイン状態 indicator / ログアウト / 再ログイン導線。
            indicator は hasFullAccess (drive.file token fresh) 基準で「🟢 = 全機能使える」を表示 (#123 で file 単一)。
            ログアウト/再ログイン導線は isLoggedIn ベース (token があれば操作可)。 */}
        <div className="flex items-center gap-2 mt-1 px-1 text-xs">
          <span
            className={`flex items-center gap-1 ${hasFullAccess ? 'text-emerald-600' : 'text-gray-400'}`}
          >
            <span aria-hidden="true">{hasFullAccess ? '🟢' : '⚪'}</span>
            <span>{hasFullAccess ? 'Google ログイン中' : '未ログイン'}</span>
          </span>
          {isLoggedIn && (
            <button
              type="button"
              onClick={logout}
              className="ml-auto px-2 py-0.5 rounded-md text-gray-500 hover:bg-gray-100 transition-colors"
            >
              ログアウト
            </button>
          )}
          {needsReauth && (
            <button
              type="button"
              onClick={reauthenticate}
              className="ml-auto px-2 py-0.5 rounded-md font-bold text-white bg-blue-500 hover:bg-blue-600 transition-colors"
            >
              Google に再ログイン
            </button>
          )}
        </div>
      </div>
      {/* issue #116 FE-2: 失敗ジョブの再投入は「即投入」から「フォームへ復元」へ置換。 onRetryJob は
          親 (FingeringPage) の onRestoreJob へそのまま委譲し、 親が履歴を閉じてトップのフォームへ抽出済み
          PDF + 元設定を復元 + スクロールする (期限切れ toast も親側)。 旧失敗 entry は残す。 */}
      <DriveHistoryModal
        isOpen={isHistoryOpen}
        items={historyItems}
        isLoading={historyLoading}
        error={historyError}
        onSelect={handleSelectHistory}
        onClose={onCloseHistory}
        onDelete={handleRequestDelete}
        deletingId={deletingId}
        activeJobs={activeJobs}
        recentlyFailedJobs={recentlyFailedJobs}
        needsDriveCheckJobs={needsDriveCheckJobs}
        onDismissFailedJob={(jobId) => dismissFailedJob(jobId)}
        onRetryJob={onRestoreJob}
      />
      <OverwriteConfirmModal
        isOpen={overwriteOpen}
        fileName={pendingFile?.name ?? ''}
        onConfirm={handleConfirmRestore}
        onCancel={handleCancelRestore}
      />
      <DeleteHistoryConfirmModal
        isOpen={pendingDeleteId !== null}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
      {deleteError && (
        <div
          role="alert"
          onClick={() => setDeleteError(null)}
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[210] max-w-[90vw] px-4 py-2 rounded-lg bg-red-600 text-white text-sm shadow-lg cursor-pointer"
        >
          {deleteError}
        </div>
      )}
      <RestoreLoadingOverlay
        isOpen={restoreLoading}
        error={restoreError}
        onDismissError={handleDismissRestoreError}
      />
    </>
  );
};

export default DriveFooterBar;
